/**
 * The torn edges of a win card's tear, shared by the ticket's two halves: the main part keeps a zigzag on
 * its bottom edge and the stub the same zigzag on its top edge, so the pieces read as one sheet ripped
 * along the perforation. Pure, so the shape is testable without a DOM.
 */
const STEP_PX = 9;
const AMPLITUDE_PX = 3;
const OFFSETS = [-0.7, 0.8, -0.3, 1, -0.8, 0.3];

export interface TearClips {
  main: string;
  stub: string;
}

export function tearClipPaths(width: number): TearClips {
  const steps = Math.max(1, Math.ceil(width / STEP_PX));
  const points: Array<[number, number]> = [];
  for (let n = 0; n <= steps; n++) {
    const x = Math.min(n * STEP_PX, width);
    const y = n === 0 || n === steps ? 0 : OFFSETS[n % OFFSETS.length] * AMPLITUDE_PX;
    points.push([x, y]);
  }
  // Both clips leave the amplitude as headroom outside the box, so a tooth never shaves the content.
  const bottom = points
    .slice()
    .reverse()
    .map(([x, y]) => `${x}px calc(100% - ${AMPLITUDE_PX - y}px)`);
  const top = points.map(([x, y]) => `${x}px ${AMPLITUDE_PX + y}px`);
  return {
    main: `polygon(0 0, ${width}px 0, ${bottom.join(', ')})`,
    stub: `polygon(${top.join(', ')}, ${width}px 100%, 0 100%)`,
  };
}

export const TEAR_MS = 620;
export const SETTLE_MS = 220;
export const TEAR_EASING = 'cubic-bezier(.25,.6,.3,1)';

/** The two pieces' resting poses at the height of the tear; `rtl` mirrors them horizontally. */
export function tearPoses(rtl: boolean): { main: string; stub: string } {
  const d = rtl ? -1 : 1;
  return {
    main: `translate(${-3 * d}px, -15px) rotate(${-1.4 * d}deg)`,
    stub: `translate(${3 * d}px, 8px) rotate(${1.8 * d}deg)`,
  };
}
